import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translations
import commonEN from './locales/en/common.json';
import sidebarEN from './locales/en/sidebar.json';
import readerEN from './locales/en/reader.json';
import cardsEN from './locales/en/cards.json';
import errorsEN from './locales/en/errors.json';
import modalsEN from './locales/en/modals.json';
import articlesEN from './locales/en/articles.json';
import settingsEN from './locales/en/settings.json';
import searchEN from './locales/en/search.json';

// Import Chinese translations
import commonZH from './locales/zh/common.json';
import sidebarZH from './locales/zh/sidebar.json';
import readerZH from './locales/zh/reader.json';
import cardsZH from './locales/zh/cards.json';
import errorsZH from './locales/zh/errors.json';
import modalsZH from './locales/zh/modals.json';
import articlesZH from './locales/zh/articles.json';
import settingsZH from './locales/zh/settings.json';
import searchZH from './locales/zh/search.json';

// Import Russian translations
import commonRU from './locales/ru/common.json';
import sidebarRU from './locales/ru/sidebar.json';
import readerRU from './locales/ru/reader.json';
import cardsRU from './locales/ru/cards.json';
import errorsRU from './locales/ru/errors.json';
import modalsRU from './locales/ru/modals.json';
import articlesRU from './locales/ru/articles.json';
import settingsRU from './locales/ru/settings.json';
import searchRU from './locales/ru/search.json';

// Import French translations
import commonFR from './locales/fr/common.json';
import sidebarFR from './locales/fr/sidebar.json';
import readerFR from './locales/fr/reader.json';
import cardsFR from './locales/fr/cards.json';
import errorsFR from './locales/fr/errors.json';
import modalsFR from './locales/fr/modals.json';
import articlesFR from './locales/fr/articles.json';
import settingsFR from './locales/fr/settings.json';
import searchFR from './locales/fr/search.json';

// Import Spanish translations
import commonES from './locales/es/common.json';
import sidebarES from './locales/es/sidebar.json';
import readerES from './locales/es/reader.json';
import cardsES from './locales/es/cards.json';
import errorsES from './locales/es/errors.json';
import modalsES from './locales/es/modals.json';
import articlesES from './locales/es/articles.json';
import settingsES from './locales/es/settings.json';
import searchES from './locales/es/search.json';

// Import Japanese translations
import commonJA from './locales/ja/common.json';
import sidebarJA from './locales/ja/sidebar.json';
import readerJA from './locales/ja/reader.json';
import cardsJA from './locales/ja/cards.json';
import errorsJA from './locales/ja/errors.json';
import modalsJA from './locales/ja/modals.json';
import articlesJA from './locales/ja/articles.json';
import settingsJA from './locales/ja/settings.json';
import searchJA from './locales/ja/search.json';

// Import Portuguese translations
import commonPT from './locales/pt/common.json';
import sidebarPT from './locales/pt/sidebar.json';
import readerPT from './locales/pt/reader.json';
import cardsPT from './locales/pt/cards.json';
import errorsPT from './locales/pt/errors.json';
import modalsPT from './locales/pt/modals.json';
import articlesPT from './locales/pt/articles.json';
import settingsPT from './locales/pt/settings.json';
import searchPT from './locales/pt/search.json';

// Import German translations
import commonDE from './locales/de/common.json';
import sidebarDE from './locales/de/sidebar.json';
import readerDE from './locales/de/reader.json';
import cardsDE from './locales/de/cards.json';
import errorsDE from './locales/de/errors.json';
import modalsDE from './locales/de/modals.json';
import articlesDE from './locales/de/articles.json';
import settingsDE from './locales/de/settings.json';
import searchDE from './locales/de/search.json';
import { UI_LANGUAGE_CODES } from '../constants/languages';

// Smart initial language detection
const getInitialLanguage = () => {
  // 1. Check localStorage first (from previous sessions)
  const stored = localStorage.getItem('reedle-ui-language');
  if (stored && UI_LANGUAGE_CODES.includes(stored)) return stored;

  // 2. Detect browser language
  const browserLang = navigator.language.split('-')[0];
  if (UI_LANGUAGE_CODES.includes(browserLang)) return browserLang;

  // 3. Default fallback
  return 'en';
};

// Translation resources
const resources = {
  en: {
    common: commonEN,
    sidebar: sidebarEN,
    reader: readerEN,
    cards: cardsEN,
    errors: errorsEN,
    modals: modalsEN,
    articles: articlesEN,
    settings: settingsEN,
    search: searchEN,
  },
  zh: {
    common: commonZH,
    sidebar: sidebarZH,
    reader: readerZH,
    cards: cardsZH,
    errors: errorsZH,
    modals: modalsZH,
    articles: articlesZH,
    settings: settingsZH,
    search: searchZH,
  },
  ru: {
    common: commonRU,
    sidebar: sidebarRU,
    reader: readerRU,
    cards: cardsRU,
    errors: errorsRU,
    modals: modalsRU,
    articles: articlesRU,
    settings: settingsRU,
    search: searchRU,
  },
  fr: {
    common: commonFR,
    sidebar: sidebarFR,
    reader: readerFR,
    cards: cardsFR,
    errors: errorsFR,
    modals: modalsFR,
    articles: articlesFR,
    settings: settingsFR,
    search: searchFR,
  },
  es: {
    common: commonES,
    sidebar: sidebarES,
    reader: readerES,
    cards: cardsES,
    errors: errorsES,
    modals: modalsES,
    articles: articlesES,
    settings: settingsES,
    search: searchES,
  },
  ja: {
    common: commonJA,
    sidebar: sidebarJA,
    reader: readerJA,
    cards: cardsJA,
    errors: errorsJA,
    modals: modalsJA,
    articles: articlesJA,
    settings: settingsJA,
    search: searchJA,
  },
  pt: {
    common: commonPT,
    sidebar: sidebarPT,
    reader: readerPT,
    cards: cardsPT,
    errors: errorsPT,
    modals: modalsPT,
    articles: articlesPT,
    settings: settingsPT,
    search: searchPT,
  },
  de: {
    common: commonDE,
    sidebar: sidebarDE,
    reader: readerDE,
    cards: cardsDE,
    errors: errorsDE,
    modals: modalsDE,
    articles: articlesDE,
    settings: settingsDE,
    search: searchDE,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'reedle-ui-language',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    react: {
      useSuspense: true,
    },
  });

export default i18n;
